import React from 'react';
import {DateTime} from 'luxon';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTrash} from '@fortawesome/free-solid-svg-icons';

import {Loading, Pagination} from 'Components/Partials';
import {FormHandler} from 'Components/Form';
import {ModalTrigger} from 'Components/Modal';
import ServiceDocumentCreateModal from 'Pages/ServiceUsers/ServiceDocumentCreateModal';
import ViewServiceDocumentModal from 'Pages/ServiceUsers/ViewServiceDocumentModal';

import {Modal, Toast} from 'Services';
import User from 'Services/User';
import DocumentsApi from 'Services/Api/Admin/ServiceUsers/Documents';
import DocumentReadsApi from 'Services/Api/Admin/ServiceUsers/DocumentReads';

/*
Care documents for a service user, with "I have read and understood".

Staff confirm each document once per version; it can't be undone and is logged with the date and time.
Replacing a document starts a new version that everyone confirms again. Admins see who has read what.
*/

const toDate = (value) => {
    const iso = typeof value === 'string' ? value : value?.iso_string;

    return iso ? DateTime.fromISO(iso) : null;
};
const fmtDate = (value) => toDate(value)?.toFormat('d LLL yyyy') ?? '';
const fmtBoth = (value) => toDate(value) ? `${fmtDate(value)} at ${toDate(value).toFormat('HH:mm')}` : '';
const groupOf = (document) => (Array.isArray(document.category) ? document.category[0] : document.category) || 'Other documents';
const plural = (count, one, many = null) => `${count} ${count === 1 ? one : (many ?? one + 's')}`;

const PILLS = {
    ok: 'bg-green-100 text-green-800',
    warn: 'bg-yellow-100 text-yellow-800',
    bad: 'bg-red-100 text-red-800',
};

const Pill = ({tone, children}) => (
    <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${PILLS[tone]}`}>{children}</span>
);

const Button = ({onClick, ghost = false, small = false, disabled = false, children, ...rest}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`rounded-full font-bold whitespace-nowrap ${small ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'}
            ${disabled ? 'bg-gray-200 text-gray-500 cursor-not-allowed' :
                ghost ? 'bg-white border-2 border-app-leading text-app-leading' : 'bg-app-supporting-yellow text-white'}`}
        {...rest}
    >
        {children}
    </button>
);

/**
 * Pages a list held in the browser.
 */
class Paged extends React.Component {
    state = {page: 1};

    render() {
        const {items, perPage = 20, children} = this.props;
        const lastPage = Math.max(1, Math.ceil(items.length / perPage));
        const page = Math.min(this.state.page, lastPage);

        return (
            <>
                {children(items.slice((page - 1) * perPage, page * perPage))}
                {items.length > perPage && (
                    <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-3">
                        <span className="text-sm text-gray-600">
                            Showing {(page - 1) * perPage + 1} to {Math.min(items.length, page * perPage)} of {items.length}
                        </span>
                        <Pagination page={page} pageCount={lastPage} goToPage={(next) => this.setState({page: next})} />
                    </div>
                )}
            </>
        );
    }
}

class CareDocumentsTab extends React.Component {
    state = {
        documents: [],
        loading: true,
        opened: {},
        signing: null,
        admin: null,
        view: User.isAdmin ? 'docs' : 'mine',
        expanded: {},
    };

    componentDidMount() {
        this.loadDocuments();

        DocumentsApi.on('create', this.loadDocuments);
        DocumentsApi.on('update', this.loadDocuments);
        DocumentsApi.on('delete', this.loadDocuments);
    }

    componentDidUpdate(prevProps) {
        // Opened straight from a link, the tab can appear before the service user has loaded.
        const id = (user) => user?.id ?? user?.[0]?.id;

        if (id(prevProps.user) !== id(this.props.user)) {
            this.loadDocuments();
        }
    }

    componentWillUnmount = () => {
        DocumentsApi.removeListener('create', this.loadDocuments);
        DocumentsApi.removeListener('update', this.loadDocuments);
        DocumentsApi.removeListener('delete', this.loadDocuments);
    };

    /**
     * @return {boolean}
     */
    get isFamily() {
        return User.belongsToGroup('Service User Family') && !User.isAdmin;
    }

    loadDocuments = async () => {
        const {user} = this.props;

        if (!user) {
            return;
        }

        const [documents, admin] = await Promise.all([
            this.isFamily ? DocumentsApi.getFamilyDocuments(user[0]?.id ?? user.id) : DocumentsApi.get({user: user.id}),
            User.isAdmin ? DocumentReadsApi.get({user: user.id}) : Promise.resolve(null),
        ]);

        this.setState({
            documents: documents?.data?.data ?? [],
            admin: admin?.success ? admin.data.data : null,
            loading: false,
        });
    };

    handleDelete = async (document) => {
        if (!window.confirm(`Delete "${document.name}"? Its read and understood log is deleted with it.`)) {
            return;
        }

        await DocumentsApi.deleteDocuments(document.id, document.user_id);
        this.loadDocuments();
    };

    open = (document) => {
        this.setState({opened: {...this.state.opened, [document.id]: true}});

        Modal.open({
            component: ViewServiceDocumentModal,
            props: {pdfUrl: document.file?.url, title: document.name},
        });
    };

    sign = async (document) => {
        this.setState({signing: document.id});

        const response = await DocumentsApi.markRead(this.props.user.id, document.id);

        this.setState({signing: null});

        if (!response.success) {
            return Toast.error();
        }

        Toast.success('Saved: read and understood.');
        this.loadDocuments();
    };

    downloadLog = () => {
        const {user} = this.props;

        DocumentReadsApi.downloadLog(user.id, `read-and-understood-${user.first_name}-${user.last_name}.csv`.toLowerCase().replace(/\s+/g, '-'));
    };

    /**
     * One staff member's status against a document, from the admin log.
     *
     * @param {string} staffId
     * @param {object} document
     * @return {{state: string, read_at: string|null}}
     */
    statusOf = (staffId, document) => {
        const reads = this.state.admin.reads.filter(read => read.user_id === staffId && read.document_id === document.id);
        const current = reads.find(read => read.version === document.version);

        if (current) return {state: 'current', read_at: current.read_at};
        if (reads.length) return {state: 'again', read_at: reads[0].read_at};
        return {state: 'none', read_at: null};
    };

    assignedStaff = () => this.state.admin.staff.filter(staff => staff.assigned);

    readCount = (document) => this.assignedStaff().filter(staff => this.statusOf(staff.id, document).state === 'current').length;

    /**
     * Who to list under a document: assigned staff (read or not), plus anyone else who has read it.
     *
     * @param {object} document
     * @return {array}
     */
    namesFor = (document) => this.state.admin.staff.filter(staff => staff.assigned || this.statusOf(staff.id, document).state !== 'none');

    // ---------------------------------------------------------------- Staff view

    renderMine() {
        const {documents, opened, signing} = this.state;
        const {user} = this.props;
        const done = documents.filter(document => document.my_read?.state === 'current').length;
        const left = documents.length - done;
        const groups = [...new Set(documents.map(groupOf))];

        return (
            <div className="space-y-5">
                <section className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-wrap items-center gap-6">
                    <div className="flex-1 min-w-[16rem]">
                        <div className="font-league-spartan font-bold text-2xl">
                            {left === 0 ? `You have read everything for ${user.first_name}` : `${plural(left, 'document')} to read for ${user.first_name}`}
                        </div>
                        <div className="text-gray-600">
                            You have read and understood {done} of {documents.length}.
                        </div>
                    </div>
                    <div className="flex-1 min-w-[12rem]">
                        <div role="img" aria-label={`${done} of ${documents.length} read`} className="h-2.5 rounded-full bg-gray-200 overflow-hidden">
                            <div className="h-2.5 bg-app-leading" style={{width: (documents.length ? Math.round(done / documents.length * 100) : 0) + '%'}} />
                        </div>
                    </div>
                </section>

                {groups.map(group => (
                    <section key={group} className="bg-white border border-gray-200 rounded-2xl" aria-label={group}>
                        <h2 className="font-league-spartan font-bold text-xl px-5 pt-5 pb-3">{group}</h2>

                        {documents.filter(document => groupOf(document) === group).map(document => {
                            const status = document.my_read ?? {state: 'none'};
                            const isOpened = !!opened[document.id];

                            return (
                                <div key={document.id} className="flex flex-wrap items-center gap-4 px-5 py-4 border-t border-gray-100">
                                    <div className="flex-1 min-w-[16rem]">
                                        <div className="font-bold">{document.name}</div>
                                        <div className="text-sm text-gray-600 flex flex-wrap items-center gap-2">
                                            <span>PDF · updated {fmtDate(document.file_updated_at ?? document.created_at)}</span>
                                            {status.state === 'again' && <Pill tone="warn">Updated since you read it on {fmtDate(status.read_at)}</Pill>}
                                            {status.state === 'none' && <Pill tone="bad">Not read yet</Pill>}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center justify-end gap-3">
                                        <Button ghost onClick={() => this.open(document)}>{isOpened ? 'Open again' : 'Open'}</Button>

                                        {status.state === 'current' ? (
                                            <span className="inline-flex items-center gap-3 rounded-xl bg-green-800 text-white font-bold text-sm px-4 py-2">
                                                <span aria-hidden="true" className="w-6 h-6 rounded-full bg-white bg-opacity-25 inline-flex items-center justify-center">✓</span>
                                                <span>
                                                    Read and understood
                                                    <span className="block font-normal">{fmtBoth(status.read_at)}</span>
                                                </span>
                                            </span>
                                        ) : (
                                            <Button
                                                disabled={!isOpened || signing === document.id}
                                                onClick={() => this.sign(document)}
                                                aria-describedby={isOpened ? undefined : `open-first-${document.id}`}
                                            >
                                                {signing === document.id ? 'Saving...' : 'I have read and understood'}
                                            </Button>
                                        )}
                                    </div>

                                    {status.state !== 'current' && !isOpened && (
                                        <div id={`open-first-${document.id}`} className="w-full text-right text-sm text-gray-600 -mt-2">
                                            Open the document first.
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </section>
                ))}

                {documents.length === 0 && <p>No documents found.</p>}

                <p className="text-sm text-gray-600">
                    Your name, the date and the time are saved when you press the button. It cannot be undone, and your manager can see it.
                </p>
            </div>
        );
    }

    // ---------------------------------------------------------------- Admin views

    renderAdmin() {
        const {admin, documents, view} = this.state;
        const {user} = this.props;

        if (!admin) {
            return <Loading/>;
        }

        const staffCount = admin.assigned_count;
        const fully = admin.documents.filter(document => this.readCount(document) === staffCount).length;
        const outstanding = admin.documents.reduce((total, document) => total + (staffCount - this.readCount(document)), 0);
        const staffBehind = this.assignedStaff().filter(staff => admin.documents.some(document => this.statusOf(staff.id, document).state !== 'current')).length;
        const readers = admin.staff.filter(staff => admin.documents.some(document => this.statusOf(staff.id, document).state === 'current')).length;

        const views = [
            {key: 'docs', label: 'By document'},
            {key: 'grid', label: 'By staff member'},
            {key: 'log', label: 'Full log'},
            {key: 'mine', label: 'My reading'},
        ];

        return (
            <div className="space-y-5">
                {view !== 'mine' && staffCount === 0 && (
                    <div className="rounded-2xl bg-yellow-50 border border-yellow-200 p-4">
                        <span className="font-bold">No staff are assigned to {user.first_name} yet.</span>{' '}
                        Only people who have read a document are listed below. Assign staff by giving them the same service as {user.first_name}.
                        <div className="mt-1 text-sm text-gray-600">{plural(readers, 'staff member has', 'staff have')} read at least one current document.</div>
                    </div>
                )}

                {view !== 'mine' && staffCount > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white border border-gray-200 rounded-2xl p-4">
                            <div className="text-sm font-bold text-gray-600">Read by everyone</div>
                            <div className="font-league-spartan font-bold text-3xl">{fully} of {admin.documents.length}</div>
                            <div className="text-sm text-gray-600">documents</div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-2xl p-4">
                            <div className="text-sm font-bold text-gray-600">Sign-offs outstanding</div>
                            <div className="font-league-spartan font-bold text-3xl">{outstanding}</div>
                            <div className="text-sm text-gray-600">across {plural(staffCount, 'staff member', 'staff')}</div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-2xl p-4">
                            <div className="text-sm font-bold text-gray-600">Staff with something to read</div>
                            <div className="font-league-spartan font-bold text-3xl">{staffBehind} of {staffCount}</div>
                            <div className="text-sm text-gray-600">assigned to {user.first_name}</div>
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2" role="group" aria-label="Choose a view">
                        {views.map(item => (
                            <button
                                key={item.key}
                                type="button"
                                aria-pressed={view === item.key}
                                onClick={() => this.setState({view: item.key})}
                                className={`rounded-full px-4 py-2 font-bold border-2 ${view === item.key ? 'bg-app-typography border-app-typography text-white' : 'bg-white border-gray-300'}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Button ghost onClick={this.downloadLog}>Download log</Button>
                        <ModalTrigger component={ServiceDocumentCreateModal} props={{user}}>
                            <Button>Upload new service user document</Button>
                        </ModalTrigger>
                    </div>
                </div>

                {view === 'docs' && this.renderByDocument()}
                {view === 'grid' && this.renderGrid()}
                {view === 'log' && this.renderLog()}
                {view === 'mine' && this.renderMine()}

                {view !== 'mine' && documents.length === 0 && <p>No documents found.</p>}
            </div>
        );
    }

    renderByDocument() {
        const {admin, documents, expanded} = this.state;
        const {user} = this.props;
        const staffCount = admin.assigned_count;
        const byId = Object.fromEntries(documents.map(document => [document.id, document]));
        const groups = [...new Set(admin.documents.map(document => document.group))];

        return groups.map(group => (
            <section key={group} className="bg-white border border-gray-200 rounded-2xl" aria-label={group}>
                <h2 className="font-league-spartan font-bold text-xl px-5 pt-5 pb-3">{group}</h2>

                {admin.documents.filter(document => document.group === group).map(document => {
                    const count = this.readCount(document);
                    const names = this.namesFor(document);
                    const readBy = names.filter(staff => this.statusOf(staff.id, document).state === 'current').length;
                    const isOpen = !!expanded[document.id] && names.length > 0;
                    const full = byId[document.id];

                    return (
                        <div key={document.id} className="flex flex-wrap items-center gap-4 px-5 py-4 border-t border-gray-100">
                            <div className="flex-1 min-w-[16rem]">
                                <div className="font-bold">{document.name}</div>
                                <div className="text-sm text-gray-600">
                                    PDF · version {document.version} · updated {fmtDate(document.file_updated_at ?? document.created_at)}
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-end gap-3">
                                {staffCount > 0 ? (
                                    <span className="flex items-center gap-2 whitespace-nowrap">
                                        <span className="w-24 h-2.5 rounded-full bg-gray-200 overflow-hidden" aria-hidden="true">
                                            <span
                                                className={`block h-2.5 ${count < staffCount ? 'bg-red-500' : 'bg-app-leading'}`}
                                                style={{width: Math.round(count / staffCount * 100) + '%'}}
                                            />
                                        </span>
                                        <span><strong>{count} of {staffCount}</strong> assigned staff</span>
                                    </span>
                                ) : (
                                    <span className="whitespace-nowrap"><strong>{plural(readBy, 'person has', 'people have')}</strong> read it</span>
                                )}
                                {names.length > 0 && (
                                    <Button
                                        ghost
                                        small
                                        aria-expanded={isOpen}
                                        onClick={() => this.setState({expanded: {...expanded, [document.id]: !isOpen}})}
                                    >
                                        {isOpen ? 'Hide names' : 'Show names'}
                                    </Button>
                                )}
                                {full && (
                                    <>
                                        <Button ghost small onClick={() => this.open(full)}>Open</Button>
                                        <ModalTrigger
                                            component={ServiceDocumentCreateModal}
                                            props={{user, document: {id: full.id, name: full.name, category: full.category}}}
                                        >
                                            <Button ghost small title="Upload a new file. Everyone will need to read it again.">Replace</Button>
                                        </ModalTrigger>
                                        <button type="button" aria-label={`Delete ${full.name}`} onClick={() => this.handleDelete(full)}>
                                            <FontAwesomeIcon icon={faTrash} className="text-gray-500 hover:text-red-700" />
                                        </button>
                                    </>
                                )}
                            </div>

                            {isOpen && (
                                <div className="w-full border border-gray-200 rounded-xl overflow-hidden">
                                    <Paged items={names} perPage={10}>
                                        {(staffPage) => staffPage.map(staff => {
                                            const status = this.statusOf(staff.id, document);

                                            return (
                                                <div key={staff.id} className={`flex flex-wrap items-center gap-3 px-4 py-2 border-t border-gray-100 first:border-t-0 ${status.state === 'current' ? '' : 'bg-red-50'}`}>
                                                    <span className="font-bold w-48">
                                                        {staff.name}
                                                        {!staff.assigned && staffCount > 0 && <span className="block font-normal text-xs text-gray-600">not assigned</span>}
                                                    </span>
                                                    <span className="flex-1">
                                                        {status.state === 'current' && <><Pill tone="ok">Read and understood</Pill> {fmtBoth(status.read_at)}</>}
                                                        {status.state === 'again' && <><Pill tone="warn">Needs to read the new version</Pill> Read the previous one on {fmtDate(status.read_at)}</>}
                                                        {status.state === 'none' && <Pill tone="bad">Not read yet</Pill>}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </Paged>
                                </div>
                            )}
                        </div>
                    );
                })}
            </section>
        ));
    }

    renderGrid() {
        const {admin} = this.state;

        if (admin.staff.length === 0) {
            return <p className="bg-white border border-gray-200 rounded-2xl p-5 text-gray-600">No staff are assigned and nobody has read a document yet.</p>;
        }

        return (
            <section className="bg-white border border-gray-200 rounded-2xl" aria-label="Who has read what">
                <p className="text-sm text-gray-600 px-5 pt-4">Each tick is a logged "read and understood", with the date it was pressed.</p>
                <div className="overflow-x-auto">
                    <Paged items={admin.staff} perPage={20}>
                        {(staffPage) => (
                            <table className="w-full text-sm" style={{minWidth: 200 + admin.documents.length * 110}}>
                                <thead>
                                    <tr>
                                        <th scope="col" className="text-left px-5 py-3 font-bold text-gray-600 align-bottom">Staff member</th>
                                        {admin.documents.map(document => (
                                            <th key={document.id} scope="col" title={document.name} className="px-2 py-3 font-bold text-gray-600 align-bottom text-center max-w-[8rem]">
                                                <span className="block truncate">{document.name}</span>
                                            </th>
                                        ))}
                                        <th scope="col" className="px-2 py-3 font-bold text-gray-600 align-bottom">Done</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {staffPage.map(staff => {
                                        const done = admin.documents.filter(document => this.statusOf(staff.id, document).state === 'current').length;

                                        return (
                                            <tr key={staff.id} className="border-t border-gray-100">
                                                <th scope="row" className="text-left px-5 py-2 whitespace-nowrap">{staff.name}</th>
                                                {admin.documents.map(document => {
                                                    const status = this.statusOf(staff.id, document);

                                                    if (status.state === 'current') {
                                                        return (
                                                            <td key={document.id} className="text-center px-2 py-2 text-green-800 font-bold">
                                                                ✓<span className="block font-normal text-gray-600">{fmtDate(status.read_at)}</span>
                                                            </td>
                                                        );
                                                    }

                                                    return status.state === 'again' ? (
                                                        <td key={document.id} className="text-center px-2 py-2 bg-yellow-50 text-yellow-800 font-bold">
                                                            Read again<span className="block font-normal">old version</span>
                                                        </td>
                                                    ) : (
                                                        <td key={document.id} className="text-center px-2 py-2 bg-red-50 text-red-800 font-bold">Not read</td>
                                                    );
                                                })}
                                                <td className="text-center px-2 py-2"><strong>{done} of {admin.documents.length}</strong></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </Paged>
                </div>
                <div className="flex flex-wrap gap-4 px-5 py-3 border-t border-gray-100 text-sm text-gray-600">
                    <span><strong>✓</strong> read and understood, current version</span>
                    <span><strong>Read again</strong> the document was replaced after they read it</span>
                    <span><strong>Not read</strong> never confirmed</span>
                </div>
            </section>
        );
    }

    renderLog() {
        const {admin} = this.state;
        const documents = Object.fromEntries(admin.documents.map(document => [document.id, document]));

        return (
            <section className="bg-white border border-gray-200 rounded-2xl" aria-label="Full log, newest first">
                {admin.reads.length === 0 && <p className="p-5 text-gray-600">Nobody has confirmed a document yet.</p>}
                <Paged items={admin.reads} perPage={25}>
                    {(reads) => (
                        <ol>
                            {reads.map(read => {
                                const document = documents[read.document_id];

                                return (
                                    <li key={read.id} className="flex flex-wrap gap-x-4 gap-y-1 px-5 py-3 border-t border-gray-100 first:border-t-0">
                                        <time dateTime={read.read_at} className="w-44 text-gray-600 tabular-nums">
                                            {fmtDate(read.read_at)}, {toDate(read.read_at)?.toFormat('HH:mm')}
                                        </time>
                                        <span>
                                            <strong>{read.user_name}</strong> read and understood <strong>{document?.name}</strong>{' '}
                                            <span className="text-gray-600">
                                                (version {read.version}{document && read.version < document.version ? ', since replaced' : ''}
                                                {read.source === 'care_plan_confirmation' ? ', from the care plan confirmation form' : ''})
                                            </span>
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </Paged>
            </section>
        );
    }

    // ---------------------------------------------------------------- Family view

    renderFamily() {
        const {documents} = this.state;
        const groups = [...new Set(documents.map(groupOf))];

        if (documents.length === 0) {
            return <p>No documents found.</p>;
        }

        return groups.map(group => (
            <section key={group} className="bg-white border border-gray-200 rounded-2xl mb-5" aria-label={group}>
                <h2 className="font-league-spartan font-bold text-xl px-5 pt-5 pb-3">{group}</h2>
                {documents.filter(document => groupOf(document) === group).map(document => (
                    <div key={document.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 border-t border-gray-100">
                        <span className="font-bold">{document.name}</span>
                        <Button ghost onClick={() => this.open(document)}>Open</Button>
                    </div>
                ))}
            </section>
        ));
    }

    render() {
        const {working} = this.props;
        const {loading} = this.state;

        if (working || loading) {
            return <Loading/>;
        }

        if (this.isFamily) {
            return this.renderFamily();
        }

        return User.isAdmin ? this.renderAdmin() : this.renderMine();
    }
}

export default FormHandler(CareDocumentsTab);
